import * as React from 'react'
import { useDispatch } from 'react-redux'
import {
  Box,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
} from '@mui/material'
import { getUserTreeCount, disableUserById, permanentlyDeleteUserById } from '../../../services'
import { setIsStored } from '../../../redux/slices/Alerts'

/**
 * Flujo de eliminación/deshabilitación de usuario a nivel de tabla (pre-check de
 * árboles asociados). Controlado de forma imperativa (`ref.openModal(user)`) para
 * sobrevivir a refetch/re-render del DataGrid.
 */
const DeleteUserModal = React.forwardRef((_props, ref) => {
  const dispatch = useDispatch()
  const [user, setUser] = React.useState(null)
  const [dialogOpen, setDialogOpen] = React.useState(false)
  const [dialogLoading, setDialogLoading] = React.useState(false)
  const [treeCount, setTreeCount] = React.useState(null)

  const runPrecheck = React.useCallback(async targetUser => {
    setTreeCount(null)
    setDialogOpen(true)
    setDialogLoading(true)
    try {
      const result = await getUserTreeCount(targetUser.id)
      setTreeCount(result?.count ?? 0)
    } catch {
      setTreeCount(0)
    } finally {
      setDialogLoading(false)
    }
  }, [])

  React.useImperativeHandle(ref, () => ({
    openModal: targetUser => {
      if (!targetUser?.id) return
      setUser(targetUser)
      runPrecheck(targetUser)
    },
  }))

  const handleDialogClose = () => {
    if (dialogLoading) return
    setDialogOpen(false)
    setTreeCount(null)
  }

  const handleConfirm = async () => {
    if (!user) return
    setDialogLoading(true)
    try {
      if (treeCount > 0) {
        const result = await disableUserById(user.id)
        dispatch(setIsStored({ stored: true, message: result?.message || 'Usuario deshabilitado' }))
      } else {
        const result = await permanentlyDeleteUserById(user.id)
        if (result?.deleted === false) {
          dispatch(setIsStored({ stored: false, message: result.message || 'No se pudo eliminar' }))
        } else {
          dispatch(setIsStored({ stored: true, message: 'Usuario eliminado permanentemente' }))
        }
      }
    } catch {
      dispatch(setIsStored({ stored: false, message: 'Error al procesar la acción' }))
    } finally {
      setDialogLoading(false)
      setDialogOpen(false)
      setTreeCount(null)
    }
  }

  const fullName = `${user?.nombre || ''} ${user?.apellido || ''}`.trim()

  return (
    <Dialog open={dialogOpen} onClose={handleDialogClose} maxWidth="xs" fullWidth>
      <DialogTitle>
        {dialogLoading
          ? 'Verificando...'
          : treeCount > 0
          ? 'Usuario con árboles asociados'
          : 'Eliminar usuario'}
      </DialogTitle>
      <DialogContent>
        {dialogLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
            <CircularProgress size={32} />
          </Box>
        ) : treeCount > 0 ? (
          <DialogContentText>
            <strong>{fullName}</strong> tiene <strong>{treeCount}</strong> árbol(es) registrado(s)
            y no puede eliminarse permanentemente.
            <br />
            <br />
            ¿Deseas <strong>deshabilitar</strong> este usuario en su lugar?
          </DialogContentText>
        ) : (
          <DialogContentText>
            ¿Eliminar permanentemente a <strong>{fullName}</strong>?
            <br />
            <br />
            Esta acción no se puede deshacer.
          </DialogContentText>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleDialogClose} disabled={dialogLoading}>
          Cancelar
        </Button>
        {!dialogLoading && (
          <Button
            onClick={handleConfirm}
            variant="contained"
            color={treeCount > 0 ? 'warning' : 'error'}
            disabled={dialogLoading}>
            {treeCount > 0 ? 'Deshabilitar' : 'Eliminar'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  )
})

DeleteUserModal.displayName = 'DeleteUserModal'

export default DeleteUserModal
